import React from 'react';
import * as PropTypes from 'prop-types';
import classnames from 'classnames';
import turfPointInPolygon from '@turf/boolean-point-in-polygon';
import lodashOmit from 'lodash/omit';
import * as turfHelpers from '@turf/helpers';
import turfPoint from 'turf-point';
import {CardDataProvider} from 'Components/common/Context/CardDataContext';
import * as locationUtils from 'Utils/locationUtils';
import MapboxMap from 'Components/common/MapboxMap/MapboxMap';
import {getIconsByType} from 'Components/layouts/kiosk/KioskLayout/kioskLayoutUtils';
import * as analytics from 'Utils/analytics';
import {
    CONFIG_DATA_BASE_KEYS,
    GEOMETRY_TYPES,
    JOURNEY_STAGES,
    LOCATION_TYPES,
    MAP_LAYERS,
    MAP_THEMES,
    SELECTED_LOCATION_FEATURE_ID,
    NAV_MAP_DEFAULT_CONFIG,
    NAV_MODES,
    PORTRAIT_ONLY,
    SELECTED_LOCATION_DISPLAY_NAME,
    SOURCES,
    MY_LOCATION_DISPLAY_NAME,
    STARTING_LOCATION_DISPLAY_NAME,
    MY_LOCATION_FEATURE_ID,
    LABEL_SIZES,
} from 'Utils/constants';
import {getCenterPoint} from 'Utils/data/mapDataUtils';
import {
    EXPLORER_NAVIGATION,
    LIVE_LOCATION_ANIMATION_DURATION_MS,
} from 'Utils/explorer/explorerConstants';
import * as mapUtils from 'Utils/explorer/mapUtils';
import * as mapManager from 'Utils/explorer/mapManager';
import log from 'Utils/log';
import FeaturePropType from 'Utils/propTypes/FeaturePropType';
import KioskConfigPropType from 'Utils/propTypes/KioskConfigPropType';
import KioskNavigationPropType from 'Utils/propTypes/KioskNavigationPropType';
import MapDataForKioskPropType from 'Utils/propTypes/MapDataForKioskPropType';
import * as routeManager from 'Utils/routeManager';
import storage from 'Utils/storage';
import {mapEasing} from 'Utils/utils';
import IMDF_FEATURE_TYPES from 'Utils/imdfFeatureTypes';
import {showDirectionsToEventLocation} from 'Utils/data/kioskDataUtils';
import {Mobile} from 'Utils/ResponsiveUtil';
import {VIEWPORT_BREAKPOINTS} from 'Utils/viewportConstants';
import {getLengthLabel} from 'Utils/unitOfMeasure';
import {getSearchBoundaryPolygon, getSearchBoundaryRadius} from 'Utils/explorer/mapUtils';
import WelcomeModal from './ExplorerModals/WelcomeModal';
import CardStack from './Cards/CardStack/CardStack';
import {getCardComponent} from './explorerUtils';
import Menu from './Menu/Menu';
import QuickLinks from './Cards/SearchCard/QuickLinks';
import ClientSwitcherModal from './ExplorerModals/ClientSwitcherModal';
import LocationInfoModal from './ExplorerModals/LocationInfoModal';
import LocationTooFarModal from './ExplorerModals/LocationTooFarModal';

import './scss/explorer.scss';

class Explorer extends React.PureComponent {
    constructor(props) {
        super(props);
        const {searchParams} = new URL(props.url);
        storage.setItem('hasAllowedLocation', false);
        this.journeyParamsString = JSON.parse(searchParams.get('route'));
        this.journeyRouteOption = searchParams.get('routeOption');
        this.searchLocationParamsString = searchParams.get('searchLocation');
        this.tenantIdParamString = searchParams.get('tenantId');
        this.eventIdParamString = searchParams.get('eventId');
        this.promotionIdParamString = searchParams.get('promotionId');
        this.mapThemeFromURL = searchParams.get('mapTheme');
        this.zoomFromURL = searchParams.get('zoom');
        this.pitchFromURL = searchParams.get('pitch');
        this.isAutoLoad = searchParams.get('autoload') === 'y';
        // Need to get this BEFORE mounting the map, since Mapbox will change the URL
        this.urlLocationHash = mapUtils.urlLocationHash(document.location);
        this.mainVenueFeature = null;
        const mapTheme =
            this.props.config?.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE].mapConfig
                .mapStyle ?? MAP_THEMES.EXPLORER_DARK;
        this.state = {
            mapReady: false,
            showWelcomeModal: !this.isAutoLoad,
            showInfoModal: false,
            showClientSwitcherModal: false,
            showLocationTooFarModal: false,
            mapTheme: this.mapThemeFromURL ?? mapTheme,
            isThemeSet: false,
            switchingStyles: false,
            centerOffset: {top: 0, bottom: 0},
            hideAttributionControls: false,
            clearFilterHandler: null,
            labelSize: LABEL_SIZES.REGULAR,
        };
        this.locationEnabledFlag = false;
        this.searchBoundaryFeature = {};
    }

    setQuickLinkFn = quickLink => {
        this.props.setQuickLink(quickLink);
        this.props.navigate('search', {
            saveSearchState: true,
            showCloseButton: this.props?.navigation?.key !== 'search',
        });
    };

    setMapLocationFromClick = feature => {
        if (this.props.navMode === NAV_MODES.SEARCH) {
            if (!feature.properties.hideFromSearch || feature.properties.tenant) {
                analytics.sendSearchEvent(
                    analytics.ACTIONS.RESULTS,
                    `Location: ${feature?.properties?.locationId} | ${
                        feature?.properties?.displayText
                    }, ${locationUtils.getAncestorsDisplayText(feature?.properties?.ancestors)} `
                );
                this.props.navigate('featureSummary', {item: feature});
                this.props.setSearchLocation({
                    feature,
                    zoomToResult: false,
                });
            }
        } else if (this.props.navMode === NAV_MODES.DIRECTIONS) {
            if (!this.props.journey.stops[0]) {
                // If a 'from' location hasn't been selected,
                // put the clicked location into that slot
                this.props.setJourneyStop({index: 0, feature, zoomToResult: false});
            } else if (!this.props.journey.stops[1]) {
                // If the 'to' location hasn't been selected, use that slot
                this.props.setJourneyStop({index: 1, feature, zoomToResult: false});
            }
        }
    };

    // Handle clicks on PAM features
    handleLabelClick = e => {
        // only do this if we arent on search screen via directions
        // TODO: this wont be needed after DEV-8472
        if (this.props.directionsJourneyIndex === undefined) {
            if (e.features && e.features.length === 1) {
                const clickedFeature = e.features[0];
                const feature = this.props.mapDataForKiosk.navMap.features.find(navMapFeature => {
                    // For a click on a feature that's a member of a group, behave as though the
                    // group was selected
                    if (clickedFeature.properties.groupId) {
                        return (
                            navMapFeature.properties.featureId === clickedFeature.properties.groupId
                        );
                    }

                    // For a click on display point, we pick the parent feature of the displayPoint
                    if (clickedFeature.properties.isDisplayPoint) {
                        return (
                            navMapFeature.properties.displayPoint &&
                            navMapFeature.properties.displayPoint.id === clickedFeature.id
                        );
                    }

                    return (
                        navMapFeature.properties.featureId === clickedFeature.properties.featureId
                    );
                });
                // If there's already a stop, and it's the same as the clicked feature, bail.
                const [start, end] = this.props.journey.stops;
                if (
                    (start && start.properties.featureId === feature.properties.featureId) ||
                    (end && end.properties.featureId === feature.properties.featureId)
                ) {
                    return;
                }

                if (feature) {
                    if (feature.properties?.events?.length) {
                        analytics.sendMapEvent(analytics.ACTIONS.TOUCH, 'Event');
                    } else if (feature.properties?.tenant) {
                        analytics.sendMapEvent(analytics.ACTIONS.TOUCH, 'Tenant', {
                            TenantId: feature.properties?.tenant?.uuid,
                            TenantName: feature.properties?.tenant?.name,
                        });
                    } else {
                        analytics.sendMapEvent(analytics.ACTIONS.TOUCH, 'Location');
                    }
                    this.setMapLocationFromClick(feature);
                } else {
                    console.warn("A location was clicked but doesn't exist:", e.features[0]);
                }
            } else {
                console.warn('Something odd, here are the clicked features:', e.features);
            }
        }
    };

    // Handle clicks on Map
    handleMapClick = e => {
        // only do this if we aren't on search screen via directions
        if (this.props.directionsJourneyIndex === undefined) {
            if (!e.features) {
                const point = this.map.unproject({x: e.point.x, y: e.point.y});
                if (
                    !turfPointInPolygon(
                        turfPoint([point.lng, point.lat]),
                        this.searchBoundaryFeature
                    )
                ) {
                    // out of bounds
                    return;
                }

                const bbox = [[e.point.x - 5, e.point.y - 5], [e.point.x + 5, e.point.y + 5]];
                const featuresUnderMouse = this.map.queryRenderedFeatures(bbox);

                const clickOnPamLabel = featuresUnderMouse.some(
                    feature => feature.source === SOURCES.LABELS
                );
                if (clickOnPamLabel) return;

                // check if click happened in pam space or outside
                let hasPamVenueFeatureUnderMouse = false;

                featuresUnderMouse.forEach(feature => {
                    if (
                        feature.source === SOURCES.NAV_MAP &&
                        feature.properties?.canForcePamRoutes &&
                        (feature.properties?.forcePamRoutes ||
                            feature.properties?.forcePamRoutes === undefined)
                    ) {
                        hasPamVenueFeatureUnderMouse = true;
                    }
                });

                if (!this.props.externalLocations?.enabled && !hasPamVenueFeatureUnderMouse) {
                    return;
                }

                const coords = e.lngLat.toArray();
                // Prevent two taps on the same place populating the 'to' and 'from'
                const firstStop = this.props.journey.stops[0];
                if (firstStop && firstStop.geometry.type === GEOMETRY_TYPES.POINT) {
                    const twoPointsAreClose = mapManager.areSimilarCoords(
                        firstStop.geometry.coordinates,
                        coords
                    );
                    if (twoPointsAreClose) return;
                }
                const featureUnderMouse = featuresUnderMouse[0];

                // If this is a Mapbox feature, it might have a name
                const displayText =
                    featureUnderMouse?.properties?.name || SELECTED_LOCATION_DISPLAY_NAME;

                // Only make "SELECTED_LOCATION_DISPLAY_NAME" to be selectable if navMode=NAV_MODES.DIRECTIONS
                if (
                    displayText !== SELECTED_LOCATION_DISPLAY_NAME ||
                    this.props.navMode === NAV_MODES.DIRECTIONS
                ) {
                    const feature = turfHelpers.point(coords);

                    feature.id = SELECTED_LOCATION_FEATURE_ID;
                    feature.properties = {
                        displayText,
                        locationType: hasPamVenueFeatureUnderMouse
                            ? LOCATION_TYPES.PAM
                            : LOCATION_TYPES.MAPBOX,
                        isProxyLocation: featureUnderMouse?.properties?.name,
                    };

                    this.setMapLocationFromClick(feature);
                }
            }
        }
    };

    useMapHandler = () => {
        if (
            window.currentUserLocation?.lat &&
            window.DeviceOrientationEvent &&
            storage.getItem('hasAllowedLocation')
        ) {
            window.userMovedMap = true;
        }
    };

    handleNavMapReady = (forceMapReady = false) => {
        if (this.isAutoLoad && 'geolocation' in navigator) {
            window.geolocateControl.trigger({isAutoLoad: true});
        }

        const bodyElement = document.getElementById('body');
        if (bodyElement) {
            bodyElement.className = '';
        }

        this.resetWindowToInitialHeight();

        this.map.on('touchstart', this.useMapHandler);
        if (!window.__isIOS__) {
            window.removeEventListener('deviceorientationabsolute', this.handleOrientation);
            window.addEventListener('deviceorientationabsolute', this.handleOrientation);
        } else {
            window.removeEventListener('deviceorientation', this.handleOrientation);
            window.addEventListener('deviceorientation', this.handleOrientation);
        }

        const {props} = this;

        this.setState({mapReady: true});
        this.props.setExplorerLoaded(true);

        this.mainVenueFeature = this.props.mapDataForKiosk.navMap.features.find(
            f => f.properties?.featureType?.imdfFeatureType?.code === IMDF_FEATURE_TYPES.venue.code
        );

        if (!this.state.showWelcomeModal || forceMapReady) {
            if (
                !this.urlLocationHash &&
                !this.journeyParamsString &&
                !this.searchLocationParamsString &&
                !this.eventIdParamString
            ) {
                mapManager.fitToGeoJson(this.props.mapDataForKiosk.navMap);
            }

            // add QR code marker to scanned location
            if (this.props.scanLocation) {
                this.props.setScanLocation(this.props.scanLocation);
            }

            // At this point, the Mapbox map is ready, and we have the navMap
            // So we can check the URL to see if a searchLocation or journey is defined
            if (this.eventIdParamString) {
                const eventId = parseInt(this.eventIdParamString, 10);
                const event = this.props.mapDataForKiosk.events.find(({id}) => id === eventId);
                if (event) {
                    this.props.setTakeMeThereEventId(null);
                    this.props.navigate('eventDetails', {item: event});
                    if (showDirectionsToEventLocation(event) && event.feature) {
                        const cleanFeature = lodashOmit(event.feature, 'icon');
                        props.setJourneyStop({index: 1, feature: cleanFeature, zoomToResult: true});
                    }
                }
            } else if (this.promotionIdParamString) {
                if (this.tenantIdParamString) {
                    try {
                        const feature = props.mapDataForKiosk.navMap.features.find(
                            i => i.properties.tenantId === this.tenantIdParamString
                        );
                        if (feature) {
                            props.setJourneyStop({index: 1, feature, zoomToResult: true});
                        }
                        this.props.navigate('promotionsDetailsCard', {
                            item: {
                                id: this.promotionIdParamString,
                                clientId: this.props.config.settings.clientId,
                            },
                        });
                    } catch (err) {
                        log.error(
                            `Could not convert tenantId for 'take me there' for promotion share: ${
                                err.message
                            }`
                        );
                    }
                }
            } else if (this.searchLocationParamsString) {
                try {
                    const feature = locationUtils.convertMinimalToFull({
                        minimalLocation: JSON.parse(this.searchLocationParamsString),
                        navMap: props.mapDataForKiosk.navMap,
                    });
                    if (feature) {
                        this.setMapLocationFromClick(feature);
                    }
                } catch (err) {
                    log.error(`Could not convert search location URL parameters: ${err.message}`);
                }
            } else if (this.journeyParamsString) {
                routeManager.whenReady(() => {
                    this.handleJourneyRoute();
                });
            } else if (this.tenantIdParamString) {
                try {
                    const feature = props.mapDataForKiosk.navMap.features.find(
                        i => i.properties.tenantId === this.tenantIdParamString
                    );
                    if (feature) {
                        this.setMapLocationFromClick(feature);
                    }
                } catch (err) {
                    log.error(`Could not convert search location URL parameters: ${err.message}`);
                }
            } else if (this.props.scanLocation) {
                this.props.setScanLocation(this.props.scanLocation);
                this.resetMap();
            } else {
                this.resetMap();
            }

            this.addSearchBoundary();
        }
    };

    getMapConfig = () => {
        const mapCenter = this.props.mapCenter;
        const mapConfig = {
            bearing:
                this.props.mapDataForKiosk.navMap.config.defaultRotation ??
                NAV_MAP_DEFAULT_CONFIG.defaultRotation,
            pitch:
                this.props.mapDataForKiosk.navMap.config.defaultNavMapPitch ??
                NAV_MAP_DEFAULT_CONFIG.defaultNavMapPitch,
            center: mapCenter,
            zoom:
                this.props.mapDataForKiosk.navMap?.config?.defaultNavMapZoom ??
                NAV_MAP_DEFAULT_CONFIG.defaultNavMapZoom,
            maxBounds: mapUtils.getBboxByRadiusFromPoint(
                turfHelpers.point([mapCenter.lng, mapCenter.lat]),
                this.props.mapDataForKiosk.navMap?.config?.mapBoundaryRadius ??
                    NAV_MAP_DEFAULT_CONFIG.mapBoundaryRadius,
                this.props.config.settings.units
            ),
        };

        if (this.props.scanLocation) {
            mapConfig.center = getCenterPoint(this.props.scanLocation);
            mapConfig.zoom = this.zoomFromURL ?? mapConfig.zoom;
            mapConfig.bearing =
                (this.props.scanLocation.properties.rotation &&
                    this.props.scanLocation.properties.rotation + 180) ??
                mapConfig.bearing;
            mapConfig.pitch = this.pitchFromURL ?? mapConfig.pitch;
        } else if (this.urlLocationHash.length >= 2) {
            mapConfig.center = [this.urlLocationHash[2], this.urlLocationHash[1]];
            mapConfig.zoom = Number(this.urlLocationHash[0]);
            mapConfig.bearing = Number(this.urlLocationHash[3] ?? mapConfig.bearing);
            mapConfig.pitch = Number(this.urlLocationHash[4] ?? mapConfig.pitch);
        }
        return mapConfig;
    };

    addSearchBoundary = () => {
        const searchBoundaryRadius = getSearchBoundaryRadius(
            this.props.mapDataForKiosk.navMap?.config?.mapBoundaryRadius ??
                NAV_MAP_DEFAULT_CONFIG.mapBoundaryRadius
        );

        this.searchBoundaryFeature = getSearchBoundaryPolygon(
            [this.props.mapCenter.lng, this.props.mapCenter.lat],
            searchBoundaryRadius,
            this.props.config.settings.units
        );

        this.searchBoundaryFeature.properties.title = this.props.externalLocations?.enabled
            ? 'Search Boundary'
            : '';

        mapManager
            .getMap()
            .getSource(SOURCES.SEARCH_BOUNDARY)
            .setData(this.searchBoundaryFeature);
    };

    resetMap = () => {
        const mapConfig = this.getMapConfig();

        mapManager.fitToGeoJson(
            this.props.mapDataForKiosk.navMap,
            {},
            {
                center: mapConfig.center,
                zoom: mapConfig.zoom,
                pitch: mapConfig.pitch,
                bearing: mapConfig.bearing,
                padding: this.state.centerOffset,
            }
        );
    };

    handleMapboxInit = map => {
        this.map = map;

        // We listen for clicks on PAM location labels, rather than shapes, since labels
        // show/hide at sensible zoom levels
        this.map.on('click', MAP_LAYERS.LABELS, this.handleLabelClick);
        // entrance labels are a separate layer... so adding an additional handler
        this.map.on('click', MAP_LAYERS.ENTRANCE_LABELS, this.handleLabelClick);

        // We listen to clicks on the map for MAPBOX features
        this.map.on('click', this.handleMapClick);
    };

    centerMap = () => {
        mapManager.getMap().easeTo({
            bearing:
                this.props.mapDataForKiosk.navMap.config.defaultRotation ??
                NAV_MAP_DEFAULT_CONFIG.defaultRotation,
            duration: 500,
        });
    };

    handleThemeSwitch = async switchToTheme => {
        const {
            state: {mapReady, mapTheme, switchingStyles, labelSize},
        } = this;
        const setState = this.setState.bind(this);

        if (!mapReady || switchingStyles) return;

        let _mapTheme =
            mapTheme === MAP_THEMES.EXPLORER_DARK
                ? MAP_THEMES.EXPLORER_LIGHT
                : MAP_THEMES.EXPLORER_DARK;

        if (switchToTheme) _mapTheme = switchToTheme;

        // Things will blow up if we try to switch while a switch is in
        // progress, so we have a flag
        setState({
            switchingStyles: true,
        });

        await mapManager.setStyle({
            navMap: this.props.mapDataForKiosk.navMap,
            mapTheme: _mapTheme,
            mapboxStyles: this.props.mapboxStyles,
            labelSize,
        });

        mapManager
            .getMap()
            .getSource(SOURCES.SEARCH_BOUNDARY)
            .setData(this.searchBoundaryFeature);

        setState({mapTheme: _mapTheme, switchingStyles: false});
    };

    handleLabelSizeSwitch = async () => {
        let labelSize = this.state.labelSize;
        const setState = this.setState.bind(this);
        switch (labelSize) {
            case LABEL_SIZES.REGULAR:
                labelSize = LABEL_SIZES.MEDIUM;
                break;
            case LABEL_SIZES.MEDIUM:
                labelSize = LABEL_SIZES.LARGE;
                break;
            case LABEL_SIZES.LARGE:
                labelSize = LABEL_SIZES.REGULAR;
                break;
            default:
        }
        setState({labelSize});

        const labelConfig = this.props.config?.settings?.mapConfig?.label?.size;
        mapManager.setLabelSize(labelConfig ? labelConfig[labelSize] : 1);
    };

    updateMap = () => {
        mapManager.renderNavMapToMap({
            navMap: this.props.mapDataForKiosk.navMap,
            mapTheme: this.state.mapTheme,
            labelSize: this.state.labelSize,
        });

        mapManager.updateMap({
            mapDataForKiosk: this.props.mapDataForKiosk,
            navigation: this.props.navigation,
            setJourneyStop: this.props.setJourneyStop,
            navigate: this.props.navigate,
            setIsMapUpdateAvailable: this.props.setIsMapUpdateAvailable,
        });
    };

    handleOrientation = event => {
        if (
            this.props?.navigation?.key === 'explore' &&
            'geolocation' in navigator &&
            storage.getItem('hasAllowedLocation') &&
            (window.geolocateControl._localState !== 'OFF' &&
                window.geolocateControl._watchState !== 'OFF') &&
            window.DeviceOrientationEvent &&
            // this is created on geolocate event
            window.currentUserLocation?.lat &&
            !window.userMovedMap &&
            window.moveMapOnLoad &&
            event?.alpha
        ) {
            const compassDir = event.webkitCompassHeading || Math.abs(event.alpha - 360);
            const center = window.currentUserLocation;
            mapManager.getMap().easeTo({
                bearing: compassDir,
                zoom:
                    this.props.mapDataForKiosk.navMap?.config?.defaultNavMapZoom ??
                    NAV_MAP_DEFAULT_CONFIG.defaultNavMapZoom,
                duration: LIVE_LOCATION_ANIMATION_DURATION_MS,
                center,
                easing: mapEasing,
                padding: {
                    top: 0,
                    bottom: 0,
                },
            });
        }
    };

    // reset the to initial height when android soft keyboard reduces
    // the portview height.
    resetWindowToInitialHeight() {
        mapManager.resize();
        if (window.innerHeight !== window.initialHeight) {
            const metaViewport = document.querySelector('meta[name=viewport]');
            metaViewport?.setAttribute(
                'content',
                `height=${window.initialHeight}px, width=device-width, initial-scale=1.0`
            );
        }

        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
        if (window.innerWidth >= VIEWPORT_BREAKPOINTS.DESKTOP) {
            document.documentElement.style.setProperty('--bottom-nav-height', '0px');
        } else if ('standalone' in window.navigator && !!window.navigator.standalone) {
            document.documentElement.style.setProperty('--bottom-nav-height', '70px');
        } else {
            document.documentElement.style.setProperty('--bottom-nav-height', '56px');
        }
    }

    static getDerivedStateFromProps(props, state) {
        // if theme is set from props once, we do not want it to apply again
        if (!props.isNavMapLoaded || state.isThemeSet) {
            return null;
        }
        const mapTheme =
            props.config?.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE].mapConfig.mapStyle;

        if (mapTheme === state.mapTheme) {
            return {
                isThemeSet: true,
            };
        }

        // else update mapTheme
        return {
            mapTheme,
            isThemeSet: true,
        };
    }

    componentDidUpdate() {
        const {props} = this;
        if (props.isMapUpdateAvailable && !!props.setIsMapUpdateAvailable) {
            this.updateMap();
        }
        if (props.userSharedLocation && !this.locationEnabledFlag) {
            this.locationEnabledFlag = true;
            if (this.journeyParamsString) {
                routeManager.whenReady(() => {
                    this.handleJourneyRoute();
                });
            }
        }
        if (
            props.config?.settings?.explorerTitle &&
            document.title !== props.config?.settings?.explorerTitle
        ) {
            document.title = props.config.settings.explorerTitle;
        }
    }

    componentDidMount() {
        window.addEventListener('resize', this.resetWindowToInitialHeight);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.resetWindowToInitialHeight);

        if (!window.__isIOS__) {
            window.removeEventListener('deviceorientationabsolute', this.handleOrientation);
        } else {
            window.removeEventListener('deviceorientation', this.handleOrientation);
        }
    }

    handleJourneyRoute = () => {
        const {props} = this;
        try {
            if (this.journeyParamsString.routeParams) {
                if (this.journeyRouteOption) {
                    props.selectRouteOption(this.journeyRouteOption);
                }
                props.setRouteParameters(this.journeyParamsString.routeParams);
            }
            const journeyParams = this.journeyParamsString.stops;

            // if original stop was from location of a user & current user is not sharing location
            if (
                journeyParams[0].displayText === MY_LOCATION_DISPLAY_NAME &&
                !window.currentUserLocation?.lat
            ) {
                // change display name of location and use original start point
                journeyParams[0].displayText = STARTING_LOCATION_DISPLAY_NAME;
            }
            if (window.currentUserLocation?.lat) {
                journeyParams[0] = {
                    type: 'MAPBOX',
                    coords: [window.currentUserLocation?.lng, window.currentUserLocation?.lat],
                    displayText: MY_LOCATION_DISPLAY_NAME,
                    id: MY_LOCATION_FEATURE_ID,
                };
            }

            const journeyFeatures = [];
            let validRoute = true;

            journeyParams.forEach(minimalLocation => {
                const feature = locationUtils.convertMinimalToFull({
                    minimalLocation,
                    navMap: props.mapDataForKiosk.navMap,
                });

                if (!feature) validRoute = false;

                journeyFeatures.push(feature);
            });

            if (!validRoute) {
                log.warn('Could not find a route between stops');
                return;
            }

            props.setNavMode(NAV_MODES.DIRECTIONS);
            journeyFeatures.forEach((feature, i) => {
                props.setJourneyStop({index: i, feature});
            });
            this.props.navigate('directions', {});
        } catch (err) {
            log.error(`Could not convert journey URL parameters: ${err.message}`);
        }
    };

    setClearFilterHandler = clearFilterHandler => {
        this.state.clearFilterHandler = clearFilterHandler;
    };

    isLocationTooFarShouldBeShown = () => {
        if (this.state.showLocationTooFarModal) {
            // always show LocationTooFarModal if user clicked geolocation control and they are out of bounds
            return true;
        }
        const clientId =
            this.props.config.settings.parentClientId || this.props.config.settings.clientId;
        const lastTimeLimitationCheck = storage.getItem(`${clientId}_locationSharingLimitation`);
        if (lastTimeLimitationCheck) {
            const millisecondsIn24Hours = 24 * 60 * 60 * 1000;
            return Date.now() - lastTimeLimitationCheck >= millisecondsIn24Hours;
        }
        return true;
    };

    saveLocationSharingLimitationTime = () => {
        const clientId =
            this.props.config.settings.parentClientId || this.props.config.settings.clientId;
        storage.setItem(`${clientId}_locationSharingLimitation`, Date.now());
    };

    render() {
        const {props, state} = this;
        const mapInitConfig = props.isNavMapLoaded && {
            ...this.getMapConfig(),
            minZoom:
                props.config.navMapWithLocations?.config?.minNavMapZoom ??
                NAV_MAP_DEFAULT_CONFIG.minNavMapZoom,
            maxZoom:
                props.config.navMapWithLocations?.config?.maxNavMapZoom ??
                NAV_MAP_DEFAULT_CONFIG.maxNavMapZoom,
        };

        const hideMapControls =
            state.showWelcomeModal ||
            state.showInfoModal ||
            !EXPLORER_NAVIGATION[props.navigation.key]?.card?.showMapControls;

        mapManager.toggleThemeControl(this.props.displayMapStyleControl);

        const hideSecondaryMapControls =
            EXPLORER_NAVIGATION[props.navigation.key]?.card?.hideSecondaryMapControls;

        const isClientSwitchingEnabled =
            props.config?.settings?.enableClientSwitching &&
            props.config?.settings?.clientList?.length > 0;

        return (
            <div id="explorer" className="app">
                {props.isNavMapLoaded && (
                    <div className="body">
                        <MapboxMap
                            mapboxStyles={props.mapboxStyles}
                            hideMapControls={hideMapControls}
                            hideAttributionControls={
                                state.showWelcomeModal ||
                                state.showInfoModal ||
                                state.hideAttributionControls
                            }
                            hideSecondaryMapControls={hideSecondaryMapControls}
                            isNavMapLoaded={props.isNavMapLoaded}
                            mapTheme={state.mapTheme}
                            mapConfig={mapInitConfig}
                            navMap={props.mapDataForKiosk.navMap}
                            routingFeatures={props.mapDataForKiosk.routingFeatures}
                            onMapboxInit={this.handleMapboxInit}
                            onNavMapReady={this.handleNavMapReady}
                            isMapUpdateAvailable={false}
                            setJourneyStage={props.setJourneyStage}
                            setNavMode={props.setNavMode}
                            setIsMapUpdateAvailable={props.setIsMapUpdateAvailable}
                            outOfBoundsHandler={props.setLocationTooFar}
                            controls={[
                                {
                                    name: 'clientSwitcher',
                                    isVisible: isClientSwitchingEnabled,
                                    clickHandler: () =>
                                        this.setState({showClientSwitcherModal: true}),
                                },
                                {
                                    name: 'compass',
                                    clickHandler: () => {
                                        // Default map bearing - North direction
                                        const defaultBearing = 0;
                                        if (this.map.getBearing() === defaultBearing) {
                                            window.userMovedMap = false;
                                        } else {
                                            mapManager
                                                .getMap()
                                                .rotateTo(
                                                    defaultBearing,
                                                    {duration: 500},
                                                    {geolocateSource: true}
                                                );
                                            window.userMovedMap = true;
                                        }
                                    },
                                },
                                {name: 'pitch'},
                                {name: 'zoom'},
                                {name: 'theme', clickHandler: this.handleThemeSwitch},
                                {name: 'labelSize', clickHandler: this.handleLabelSizeSwitch},
                                {
                                    name: 'geoLocation',
                                    clickHandler: () => {
                                        this.setState({showLocationTooFarModal: true});
                                    },
                                    errorHandler: () => {
                                        this.setState({showInfoModal: true});
                                    },
                                    auto: true,
                                    zoom:
                                        this.props.mapDataForKiosk.navMap?.config
                                            ?.defaultNavMapZoom ??
                                        NAV_MAP_DEFAULT_CONFIG.defaultNavMapZoom,
                                },
                            ]}
                            hideAttribution
                            showExplorerAttribution
                            customAttributionText="© PAM eXplorer"
                        />

                        <div
                            className={classnames('overlay', {
                                cardsOverlay: props.navigation.key !== 'explore',
                                'hide-search-card': ['journey', 'featureSummary'].includes(
                                    props.navigation.key
                                ),
                            })}
                            data-testid="explorer-overlay"
                        >
                            <CardDataProvider
                                value={{
                                    mapCenter: this.props.mapCenter,
                                    mapDataForKiosk: props.mapDataForKiosk,
                                    config: props.config,
                                    navigationHistory: props.navigationHistory,
                                    navMode: props.navMode,
                                    scanLocation: props.scanLocation,
                                    setClearFilterHandler: this.setClearFilterHandler,
                                }}
                            >
                                <CardStack
                                    navigateBack={props.navigateBack}
                                    items={getIconsByType('kiosk-menu')}
                                    hideCard={props.hideCard}
                                    navItem={EXPLORER_NAVIGATION[props.navigation.key]}
                                    getCardComponent={getCardComponent}
                                    selectedQuickLink={props.selectedQuickLink}
                                    navigationHistory={props.navigationHistory}
                                    signConfig={props.config.signConfig}
                                />
                            </CardDataProvider>
                        </div>
                    </div>
                )}

                <Mobile>
                    {props.isNavMapLoaded &&
                        !props.selectedQuickLink &&
                        !props.query &&
                        props.directionsJourneyIndex === undefined &&
                        !(
                            state.showWelcomeModal ||
                            state.showInfoModal ||
                            !!props.locationTooFar
                        ) &&
                        ['search', 'explore'].includes(props?.navigation?.key) && (
                            <QuickLinks
                                quickLinks={props.quickLinks}
                                searchFilters={props.searchFilters}
                                selectQuickLink={this.setQuickLinkFn}
                            />
                        )}
                </Mobile>

                {props.isNavMapLoaded && (
                    <Menu
                        selectedMenuItemKey={props.navigation.key}
                        menuTabConfig={
                            props.config.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE]?.menuTab
                        }
                        hideCard={props.hideCard}
                        numberOfEvents={props.numberOfEvents}
                        navigate={(...args) => {
                            if (state.clearFilterHandler) {
                                state.clearFilterHandler();
                            }
                            props.navigate(...args);
                            // clear route
                            props.setJourneyStop({
                                index: 1,
                                feature: null,
                                reset: true,
                            });
                        }}
                        setJourneyStage={props.setJourneyStage}
                        setJourneyStop={props.setJourneyStop}
                        quickLinks={props.quickLinks}
                        searchFilters={props.searchFilters}
                        setQuickLinkFn={this.setQuickLinkFn}
                        projectName={props.config?.settings?.projectName}
                        brandingImageUrl={props.config?.settings?.logo}
                        resetMap={() => {
                            const mapConfig = this.getMapConfig();
                            mapManager.getMap().flyTo({
                                center: mapConfig.center,
                                zoom: mapConfig.zoom,
                                pitch: mapConfig.pitch,
                                bearing: mapConfig.bearing,
                                padding: this.state.centerOffset,
                                duration: 1500,
                            });
                        }}
                    />
                )}
                {(state.showWelcomeModal ||
                    state.showInfoModal ||
                    state.showClientSwitcherModal ||
                    !!props.locationTooFar) && (
                    <div>
                        {state.showWelcomeModal &&
                            state.mapReady && (
                                <WelcomeModal
                                    welcomeImage={
                                        props.config?.signConfig[
                                            CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE
                                        ]?.idleScreen?.welcomeImage?.url
                                    }
                                    settings={props.config?.settings}
                                    onClose={() => {
                                        this.setState({showWelcomeModal: false});
                                        this.handleNavMapReady(true);
                                    }}
                                    setNavMode={props.setNavMode}
                                    navigate={props.navigate}
                                />
                            )}
                        {!state.showWelcomeModal &&
                            state.showInfoModal && (
                                <div className="overlay infoPanelOverlay infoPanelBg">
                                    <LocationInfoModal
                                        settings={props.config.settings}
                                        onClose={() => {
                                            this.setState({showInfoModal: false});
                                        }}
                                        setNavMode={props.setNavMode}
                                        navigate={props.navigate}
                                    />
                                </div>
                            )}
                        {!state.showWelcomeModal &&
                            !!props.locationTooFar &&
                            this.isLocationTooFarShouldBeShown() && (
                                <div className="overlay infoPanelOverlay infoPanelBg">
                                    <LocationTooFarModal
                                        onClose={() => {
                                            this.resetMap();
                                            props.setLocationTooFar(false);
                                            this.saveLocationSharingLimitationTime();
                                            this.setState({showLocationTooFarModal: false});
                                        }}
                                        mapBoundaryRadius={
                                            this.props.mapDataForKiosk?.navMap?.config
                                                ?.mapBoundaryRadius ??
                                            NAV_MAP_DEFAULT_CONFIG.mapBoundaryRadius
                                        }
                                        unitOfMeasure={getLengthLabel(
                                            this.props.config?.settings?.units,
                                            true
                                        )}
                                    />
                                </div>
                            )}
                        {state.showClientSwitcherModal && (
                            <ClientSwitcherModal
                                settings={props.config.settings}
                                onClose={() => this.setState({showClientSwitcherModal: false})}
                            />
                        )}
                    </div>
                )}
                <div className="mobile-landscape-alert">
                    <div>
                        <img
                            alt="portrait only"
                            src={PORTRAIT_ONLY}
                            className="desktop-branding-image"
                        />
                        <span>PAM only supports</span>
                        <span>portrait mode on</span>
                        <span>mobile devices</span>
                    </div>
                </div>
            </div>
        );
    }
}

Explorer.propTypes = {
    config: KioskConfigPropType,
    directionsJourneyIndex: PropTypes.number,
    navigateBack: PropTypes.func,
    mapDataForKiosk: MapDataForKioskPropType,
    journey: PropTypes.shape({
        stage: PropTypes.oneOf(Object.values(JOURNEY_STAGES)),
        stops: PropTypes.arrayOf(FeaturePropType),
    }),
    numberOfEvents: PropTypes.number,
    isNavMapLoaded: PropTypes.bool,
    hideCard: PropTypes.func,
    setJourneyStop: PropTypes.func,
    navMode: PropTypes.oneOf(Object.values(NAV_MODES)),
    navigationHistory: PropTypes.array,
    setExplorerLoaded: PropTypes.func.isRequired,
    quickLinks: PropTypes.array,
    selectedQuickLink: PropTypes.object,
    query: PropTypes.string,
    searchFilters: PropTypes.array,
    setNavMode: PropTypes.func,
    setRouteParameters: PropTypes.func,
    selectRouteOption: PropTypes.func,
    setSearchLocation: PropTypes.func,
    setLocationTooFar: PropTypes.func,
    setJourneyStage: PropTypes.func,
    setQuickLink: PropTypes.func,
    setTakeMeThereEventId: PropTypes.func,
    setScanLocation: PropTypes.func,
    scanLocation: PropTypes.object,
    isMapUpdateAvailable: PropTypes.bool,
    userSharedLocation: PropTypes.bool,
    locationTooFar: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    setIsMapUpdateAvailable: PropTypes.func,
    navigation: KioskNavigationPropType,
    navigate: PropTypes.func,
    mapboxStyles: PropTypes.object,
    url: PropTypes.object,
    displayMapStyleControl: PropTypes.bool,
    mapCenter: PropTypes.object,
    externalLocations: PropTypes.object,
};

export default Explorer;
